import { useMemo } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import ListItem from '@mui/material/ListItem';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import CardView from '../../components/Redesing/card-view';
import { PropertyList } from '../../components/Redesing/property-list';
import { SeverityPill } from '../../components/Redesing/severity-pill';
import { buildLocationHoursView, LocationHoursSystem } from '../../util/operatingHours';

interface LocationOperatingHoursCardProps {
  /** The location from useLocationDetailRelayQuery (operatingHours + system). */
  location: {
    operatingHours?: unknown;
    system?: LocationHoursSystem | null;
  } | null;
  isLoading?: boolean;
  /** Only for tests: the moment whose UTC offset is used for the conversion. */
  now?: Date;
}

/**
 * Read-only view of a station's operating hours, converted from ant's UTC-day
 * windows to the system's time zone. There is no way to edit them yet.
 */
function LocationOperatingHoursCard({
  location,
  isLoading,
  now,
}: LocationOperatingHoursCardProps) {
  const view = useMemo(
    () =>
      buildLocationHoursView({
        operatingHours: location?.operatingHours,
        system: location?.system,
        now,
      }),
    [location?.operatingHours, location?.system, now]
  );
  const { timeZone } = view;

  // Without the location we can't tell "no station hours" from "not loaded yet".
  if (!location && !isLoading) return null;

  const content = (
    <Box data-id="location-operating-hours">
      {!view.enforced && (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          flexWrap="wrap"
          sx={{ px: 2, pt: 1 }}
        >
          <SeverityPill color="warning">Not enforced</SeverityPill>
          <Typography variant="body2" color="text.secondary">
            Time Restrictions is off in Settings › Rentals
          </Typography>
        </Stack>
      )}

      {view.hasUnreadableEntries && (
        <Typography variant="body2" color="text.secondary" sx={{ px: 2, pt: 1.5 }}>
          Some station hours are unreadable — ant fails to start a rental when it reaches
          them, so those times show as closed.
        </Typography>
      )}

      {view.unreadable ? (
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="body2">
            Station hours are set but unreadable — no time limit is enforced
          </Typography>
        </Box>
      ) : view.stationHours ? (
        <PropertyList>
          {view.stationHours.map(({ day, local }) => (
            <ListItem key={day} divider sx={{ px: 2, py: 1.5 }}>
              <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
                <Typography variant="body2" sx={{ minWidth: 88 }}>
                  {day}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {local}
                </Typography>
              </Stack>
            </ListItem>
          ))}
        </PropertyList>
      ) : (
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="body2">
            No station hours — the system's hours apply
          </Typography>
          {view.systemHours ? (
            <Typography variant="body2" color="text.secondary">
              {`Every day: ${view.systemHours.local}`}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              The system has no hours set.
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );

  // While the location loads there is no system yet: don't claim UTC.
  const title = !location ? (
    'Operating Hours'
  ) : (
    <Stack component="span" direction="row" spacing={0.5} alignItems="center">
      <span>{`Operating Hours · ${timeZone.abbreviation}`}</span>
      <Tooltip
        title={
          timeZone.valid
            ? `Times in ${timeZone.timeZone} (${timeZone.abbreviation}, ${timeZone.offsetLabel}). Hours are stored in UTC, so after a daylight-saving change they shift by one hour.`
            : 'Times in UTC: this system has no valid time zone.'
        }
        arrow
        describeChild
      >
        {/* A real button so keyboard and screen-reader users reach the tooltip;
            no padding so the icon sits where it did. */}
        <IconButton size="small" aria-label="About these times" sx={{ p: 0 }}>
          <InfoOutlinedIcon fontSize="small" color="action" />
        </IconButton>
      </Tooltip>
    </Stack>
  );

  return <CardView isLoading={isLoading} content={content} title={title} />;
}

export default LocationOperatingHoursCard;
